/**
 * Prefetch cache.
 *
 * Holds hover-initiated fetches keyed by URL. Each entry tracks its
 * own lifecycle (pending/resolved/rejected) so the navigation
 * controller can decide synchronously between three paths when the
 * user actually clicks:
 *
 * 1. Reuse a resolved result (the optimal hover-then-click flow).
 * 2. Adopt a pending promise when the fetch is still in flight.
 * 3. Discard a rejected entry and start a fresh fetch.
 *
 * The entry class owns the AbortController for its underlying
 * fetch and exposes abort() so mouseover-on-a-different-link can
 * cancel the previous hover's request.
 */

import type { FetchResult, NavScope } from './types';

type EntryStatus = 'pending' | 'resolved' | 'rejected';

export type Fetcher = (
  url: string,
  signal: AbortSignal,
  scope: NavScope,
) => Promise<FetchResult>;

export class PrefetchEntry {
  readonly url: string;
  readonly scope: NavScope;
  readonly promise: Promise<FetchResult>;
  private readonly controller: AbortController;
  private state: EntryStatus = 'pending';
  private resolvedResult: FetchResult | null = null;

  constructor(url: string, scope: NavScope, fetcher: Fetcher) {
    this.url = url;
    this.scope = scope;
    this.controller = new AbortController();
    this.promise = fetcher(url, this.controller.signal, scope)
      .then((result) => {
        this.state = 'resolved';
        this.resolvedResult = result;
        return result;
      })
      .catch((err: unknown) => {
        this.state = 'rejected';
        throw err;
      });
    // Observe the rejection on a side branch: a hover-initiated
    // prefetch that fails (or is aborted) before any click adopts
    // `promise` would otherwise surface as an unhandled promise
    // rejection. Adopters that await `promise` still receive the
    // error through the rethrowing branch above.
    this.promise.catch(() => {});
  }

  get status(): EntryStatus {
    return this.state;
  }

  get result(): FetchResult | null {
    return this.resolvedResult;
  }

  abort(): void {
    if (this.state === 'pending') {
      this.controller.abort();
      this.state = 'rejected';
    }
  }
}

export class PrefetchCache {
  private readonly entries = new Map<string, PrefetchEntry>();

  constructor(private readonly fetcher: Fetcher) {}

  has(url: string): boolean {
    return this.entries.has(url);
  }

  get(url: string): PrefetchEntry | undefined {
    return this.entries.get(url);
  }

  /**
   * Starts a prefetch for `url` when one is not already in flight.
   * Returns the entry whether it was just created or already cached.
   */
  prefetch(url: string, scope: NavScope): PrefetchEntry {
    const existing = this.entries.get(url);
    if (existing) {
      return existing;
    }
    const entry = new PrefetchEntry(url, scope, this.fetcher);
    this.entries.set(url, entry);
    return entry;
  }

  /**
   * Removes an entry without aborting it. Used by the controller
   * after it adopts a cached entry for a navigation.
   */
  take(url: string): PrefetchEntry | undefined {
    const entry = this.entries.get(url);
    if (entry) {
      this.entries.delete(url);
    }
    return entry;
  }

  /**
   * Aborts and forgets an in-flight entry for a URL. No-op if the
   * entry is absent or already settled.
   */
  abort(url: string): void {
    const entry = this.entries.get(url);
    if (!entry) return;
    entry.abort();
    this.entries.delete(url);
  }
}
